import { ReactNode, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import type CurrencyCode from '@alltrails/core/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import type Coupon from '@alltrails/types/Coupon';
import Typography from '@alltrails/core/components/Typography';
import type CountryCode from '@alltrails/core/types/CountryCode';
import CheckoutBase from '@alltrails/payment/components/CheckoutBase';
import { getDiscountPercentage, getPriceTotal, isTrial } from '@alltrails/payment/utils/plan';
import useUser from '@alltrails/context/hooks/useUser';
import useSetUser from '@alltrails/context/hooks/useSetUser';
import handlePaymentFormSuccess from '@alltrails/payment/utils/handlePaymentFormSuccess';
import getPlusCheckoutTrialDurationText from '@alltrails/payment/utils/getPlusCheckoutTrialDurationText';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import PaymentFinePrint from '@alltrails/payment/components/PaymentFinePrint/PaymentFinePrint';
import createSubscription from '@alltrails/payment/utils/createSubscription';
import { type SubscriptionTier } from '@alltrails/types/SubscriptionTier';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import createSubscriptionUpgrade from '@alltrails/payment/utils/createSubscriptionUpgrade';
import useRequestCountryCode from '@alltrails/context/hooks/useRequestCountryCode';
import useApplePayConfig from '@alltrails/payment/hooks/useApplePayConfig';
import type SubscriptionsNewResponse from '@alltrails/core/types/SubscriptionsNewResponse';
import useShowPlusTaxesText from '@alltrails/payment/hooks/useShowPlusTaxesText';
import styles from './CheckoutModal.module.scss';

function promotionFinePrint(couponCode: string) {
  if (couponCode.startsWith('tmt24')) {
    return (
      <Typography color="secondary" variant="paragraph100" component="div">
        Limited-time offer; qualifying wireless plan required. See T-Mobile Tuesdays in T Life app for details.
      </Typography>
    );
  }

  return null;
}

type CheckoutModalBaseProps = {
  billedHeading?: ReactNode;
  billedSubheading?: ReactNode;
  billedSubheadingRight?: ReactNode;
  billedToday: ReactNode;
  buttonText: ReactNode;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  heading: ReactNode;
  plan: Plan;
  isGooglePayAvailable?: boolean;
  setCurrencyCode: (currencyCode: CurrencyCode) => void;
  setCoupon?: (arg0: Coupon) => void;
  setPlanToAnnual: () => void;
  subheading?: ReactNode;
  userId: number;
  setNonTrialPlan: (plan: Plan) => void;
  setPlan: (plan: Plan) => void;
  navigateToWelcome: () => void;
  onSuccess?: () => void;
  subscriptionTier: SubscriptionTier;
  purchaseTier: PurchaseTier;
  trigger: UpsellTrigger;
  subscriptionsNewResponse: SubscriptionsNewResponse;
};

function CheckoutModalBase({
  billedHeading,
  billedSubheading,
  billedSubheadingRight,
  billedToday,
  buttonText,
  coupon,
  currencyCode,
  heading,
  navigateToWelcome,
  onSuccess,
  isGooglePayAvailable,
  plan,
  purchaseTier,
  setCoupon,
  setCurrencyCode,
  setNonTrialPlan,
  setPlan,
  setPlanToAnnual,
  subheading,
  subscriptionTier,
  trigger,
  userId,
  subscriptionsNewResponse
}: CheckoutModalBaseProps) {
  const setUser = useSetUser();
  const user = useUser()!;
  const [billingCountry, setBillingCountry] = useState(subscriptionsNewResponse.countryCode);

  const canUpgradeExistingSubscription = user.canUpgradeExistingSubscription;

  const applePayConfig = useApplePayConfig({
    canUpgradeExistingSubscription,
    coupon,
    plan,
    purchaseTier,
    subscription: subscriptionsNewResponse.subscription
  });

  return (
    <CheckoutBase
      applePayConfig={applePayConfig}
      billedHeading={billedHeading}
      billedSubheading={billedSubheading}
      billedSubheadingRight={billedSubheadingRight}
      billedToday={billedToday}
      billingCountry={billingCountry}
      buttonText={buttonText}
      coupon={coupon}
      currencyCode={currencyCode}
      finePrint={
        <PaymentFinePrint
          coupon={coupon}
          currencyCode={currencyCode}
          isCouponApplied={Boolean(coupon)}
          plan={plan}
          subscriptionTier={subscriptionTier}
        />
      }
      {...(coupon ? { promoText: promotionFinePrint(coupon.code) } : {})}
      heading={heading}
      isUpgrade={user.subscriptionTier === 'plus' && purchaseTier === 'peak'}
      onBillingCountryChange={(value: CountryCode, response) => {
        setBillingCountry(value);
        setCurrencyCode(response.currencyCode);
        setNonTrialPlan(response.nonTrial);

        // similar logic in pro_controllers_helper.rb for initial load wonder if we can consolidate in one spot?
        if (!coupon && (!user || user.isPromoEligible)) {
          setPlan(response.trial);
        } else {
          setPlan(response.nonTrial);
        }
      }}
      onApplePayToken={async ({ apple_pay_token, recaptcha }) => {
        const response = await (canUpgradeExistingSubscription
          ? createSubscriptionUpgrade({
              ...(coupon && { coupon_code: coupon.code }),
              apple_pay_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger
            })
          : createSubscription({
              billing_country: billingCountry,
              apple_pay_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger,
              user: {
                user_entered_coupon_code: coupon ? coupon.code : undefined
              }
            }));

        setUser(response.user);

        handlePaymentFormSuccess({
          userId,
          currencyCode,
          plan,
          webProPurchaseLocation: 'modal',
          paymentMethod: 'apple_pay',
          invoiceUuid: response.invoice_id,
          coupon,
          trigger
        });

        onSuccess?.();
        navigateToWelcome();
      }}
      onGooglePayToken={async ({ google_pay_token, recaptcha }) => {
        const response = await (canUpgradeExistingSubscription
          ? createSubscriptionUpgrade({
              ...(coupon && { coupon_code: coupon.code }),
              google_pay_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger
            })
          : createSubscription({
              billing_country: billingCountry,
              google_pay_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger,
              user: {
                user_entered_coupon_code: coupon ? coupon.code : undefined
              }
            }));

        setUser(response.user);

        handlePaymentFormSuccess({
          userId,
          currencyCode,
          plan,
          webProPurchaseLocation: 'modal',
          paymentMethod: 'google_pay',
          invoiceUuid: response.invoice_id,
          coupon,
          trigger
        });

        onSuccess?.();
        navigateToWelcome();
      }}
      onPayPalToken={async ({ paypal_token, recaptcha }) => {
        const response = await (canUpgradeExistingSubscription
          ? createSubscriptionUpgrade({
              ...(coupon && { coupon_code: coupon.code }),
              paypal_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger
            })
          : createSubscription({
              billing_country: billingCountry,
              paypal_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger,
              user: {
                user_entered_coupon_code: coupon ? coupon.code : undefined
              }
            }));

        setUser(response.user);

        handlePaymentFormSuccess({
          userId,
          currencyCode,
          plan,
          webProPurchaseLocation: 'modal',
          paymentMethod: 'paypal',
          invoiceUuid: response.invoice_id,
          coupon,
          trigger
        });

        onSuccess?.();
        navigateToWelcome();
      }}
      onSubmitTokenizedCardForm={async ({ cc_token, recaptcha }) => {
        const response = await (canUpgradeExistingSubscription
          ? createSubscriptionUpgrade({
              ...(coupon && { coupon_code: coupon.code }),
              cc_token,
              price_code: plan.priceCode,
              recaptcha,
              uid: userId,
              upsell_trigger: trigger
            })
          : createSubscription({
              billing_country: billingCountry,
              recaptcha,
              cc_token,
              price_code: plan.priceCode,
              uid: userId,
              upsell_trigger: trigger,
              user: {
                user_entered_coupon_code: coupon ? coupon.code : undefined
              }
            }));

        setUser(response.user);

        handlePaymentFormSuccess({
          userId,
          currencyCode,
          plan,
          webProPurchaseLocation: 'modal',
          paymentMethod: 'credit_card',
          invoiceUuid: response.invoice_id,
          coupon,
          trigger
        });

        onSuccess?.();
        navigateToWelcome();
      }}
      plan={plan}
      purchaseTier={purchaseTier}
      isCheckoutModal
      isGooglePayAvailable={isGooglePayAvailable}
      setCoupon={setCoupon}
      setPlanToAnnual={setPlanToAnnual}
      subheading={subheading}
      trigger={trigger}
      webProPurchaseLocation="modal"
    />
  );
}

type CheckoutProps = {
  initialNonTrialPlan: Plan;
  initialPlan: Plan;
  isGooglePayAvailable?: boolean;
  navigateToWelcome: () => void;
  onSuccess?: () => void;
  purchaseTier: PurchaseTier;
  subscriptionTier: SubscriptionTier;
  trigger: UpsellTrigger;
  userId: number;
  subscriptionsNewResponse: SubscriptionsNewResponse;
};

// Since this is an experiment, it is easier to copy this code from PlusCheckout than trying to create a PlusCheckoutBase component
// https://www.figma.com/design/dOj6Te4I0TVguYevn6Fj5g/Web-Checkout-Improvements?node-id=268-59615&t=aGFQDG4oH7HNoMzN-0
export default function CheckoutModal({
  initialNonTrialPlan,
  initialPlan,
  isGooglePayAvailable,
  navigateToWelcome,
  onSuccess,
  purchaseTier,
  subscriptionTier,
  trigger,
  userId,
  subscriptionsNewResponse
}: CheckoutProps) {
  const [coupon, setCoupon] = useState<Coupon>();
  const [currencyCode, setCurrencyCode] = useState(subscriptionsNewResponse.currencyCode);
  const [nonTrialPlan, setNonTrialPlan] = useState(initialNonTrialPlan);

  const [plan, setPlan] = useState(initialPlan);

  const requestCountryCode = useRequestCountryCode();
  const showPlusTaxesText = useShowPlusTaxesText();

  function setPlanToAnnual() {
    setPlan(nonTrialPlan);
  }

  const annualCost = <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />;

  // Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c24f591c20f2d3669a8ea
  // Desktop: https://www.figma.com/file/vJ30CAAm8qdbDO0j5C4Ce7/Web-Pro-Purchase-Flow?node-id=685%3A35997
  // Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c238b04a41125e91d0422

  if (coupon && coupon.type !== 'FreeTrialCoupon') {
    const percent = getDiscountPercentage(plan, coupon) / 100;

    function getHeading() {
      return purchaseTier === 'peak' ? (
        <FormattedMessage defaultMessage="Save {percent} on Peak" values={{ percent: <FormattedNumber style="percent" value={percent} /> }} />
      ) : (
        <FormattedMessage defaultMessage="Save {percent} on Plus" values={{ percent: <FormattedNumber style="percent" value={percent} /> }} />
      );
    }

    return (
      <CheckoutModalBase
        billedSubheading={<FormattedMessage defaultMessage="Discount" />}
        billedSubheadingRight={<FormattedMessage defaultMessage="Save {percent, number, ::percent}" values={{ percent }} />}
        billedToday={
          <>
            <s className={styles.s}>{annualCost}</s> <FormattedNumber currency={currencyCode} style="currency" value={getPriceTotal(plan, coupon)} />
          </>
        }
        buttonText={<FormattedMessage defaultMessage="Save today" />}
        coupon={coupon}
        currencyCode={currencyCode}
        heading={getHeading()}
        navigateToWelcome={navigateToWelcome}
        onSuccess={onSuccess}
        plan={plan} // this is always non trial so we could always pass nonTrial
        purchaseTier={purchaseTier}
        isGooglePayAvailable={isGooglePayAvailable}
        setCoupon={setCoupon}
        setCurrencyCode={setCurrencyCode}
        setNonTrialPlan={setNonTrialPlan}
        setPlan={setPlan}
        setPlanToAnnual={setPlanToAnnual}
        subscriptionTier={subscriptionTier}
        trigger={trigger}
        userId={userId}
        subscriptionsNewResponse={subscriptionsNewResponse}
      />
    );
  }

  if (isTrial(plan, coupon)) {
    const trialDurationText = getPlusCheckoutTrialDurationText(purchaseTier, coupon?.freeTrialDuration);

    return (
      <CheckoutModalBase
        billedSubheading={<FormattedMessage defaultMessage="Billed after trial" />}
        billedSubheadingRight={
          <FormattedMessage
            defaultMessage="{annualCost} annually{plusTaxes, select, true { + tax} other {}}"
            values={{ annualCost, plusTaxes: showPlusTaxesText }}
          />
        }
        billedToday={<FormattedNumber currency={currencyCode} style="currency" value={0} />}
        buttonText={<FormattedMessage defaultMessage="Start your free trial" />}
        coupon={coupon}
        currencyCode={currencyCode}
        heading={trialDurationText}
        navigateToWelcome={navigateToWelcome}
        onSuccess={onSuccess}
        plan={plan}
        purchaseTier={purchaseTier}
        isGooglePayAvailable={isGooglePayAvailable}
        setCoupon={setCoupon}
        setCurrencyCode={setCurrencyCode}
        setNonTrialPlan={setNonTrialPlan}
        setPlan={setPlan}
        setPlanToAnnual={setPlanToAnnual}
        subheading={
          requestCountryCode === 'US' ? (
            <FormattedMessage defaultMessage="Then just {annualCost} a year after your trial." values={{ annualCost }} />
          ) : (
            <FormattedMessage
              defaultMessage="Then just {annualCost} a year after your trial. No commitment, cancel anytime."
              values={{ annualCost }}
            />
          )
        }
        subscriptionTier={subscriptionTier}
        trigger={trigger}
        userId={userId}
        subscriptionsNewResponse={subscriptionsNewResponse}
      />
    );
  }

  function getHeading() {
    return purchaseTier === 'peak' ? (
      <FormattedMessage defaultMessage="Explore to the fullest with Peak" />
    ) : (
      <FormattedMessage defaultMessage="Adventure offline with Plus" />
    );
  }

  // Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c23891dd05a2a732656a0
  // Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c24a858c4c42d0bd73f80
  return (
    <CheckoutModalBase
      billedToday={annualCost}
      buttonText={<FormattedMessage defaultMessage="Upgrade" />}
      currencyCode={currencyCode}
      heading={getHeading()}
      navigateToWelcome={navigateToWelcome}
      onSuccess={onSuccess}
      plan={plan} // this is always non trial so we could always pass nonTrial
      purchaseTier={purchaseTier}
      isGooglePayAvailable={isGooglePayAvailable}
      setCoupon={setCoupon}
      setCurrencyCode={setCurrencyCode}
      setNonTrialPlan={setNonTrialPlan}
      setPlan={setPlan}
      setPlanToAnnual={setPlanToAnnual}
      subscriptionTier={subscriptionTier}
      trigger={trigger}
      userId={userId}
      subscriptionsNewResponse={subscriptionsNewResponse}
    />
  );
}
